import React, { useState, useEffect } from "react";
import { message, Tabs } from "antd";
import { Footer, Sidebar } from "../../../components/index.js";
import dayjs from "dayjs";
import Dropdowns from "./Dropdowns.jsx";
import {
  CalendarOutlined,
  DeploymentUnitOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import isBetween from "dayjs/plugin/isBetween";
import customParseFormat from "dayjs/plugin/customParseFormat";
import RecommendedHarvestSchedule from "./RecommendedHarvestSchedule.jsx";
import HarvestRequirement from "./HarvestRequirement.jsx";
import HarvestConfig from "./HarvestConfig.jsx";

/** @typedef {import("./harvestOptimizer.types.d.ts").HarvestOptimizerProps} HarvestOptimizerProps */

dayjs.extend(isBetween);
dayjs.extend(customParseFormat);

const TAB_KEYS = {
  RECOMMENDED: "recommended",
  REQUIREMENT: "requirement",
  CONFIG: "config",
};

/** @param {HarvestOptimizerProps} props */
const HarvestOptimizer = ({
  currentOrg,
  setCurrentOrg,
  orgData,
  setOrgData,
  orgLoading,
  setOrgLoading,
  siteData,
  setSiteData,
  currentSite,
  setCurrentSite,
  siteLoading,
  setSiteLoading,
  currentCrop,
  setCurrentCrop,
  cropLoading,
  setCropLoading,
  cropData,
  setCropData,
  searchParams,
  setSearchParams,
  settingDefaults,
  setSettingDefaults,
}) => {
  const [messageApi, contextHolder] = message.useMessage();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [greenHouseLabel, setGreenHouseLabel] = useState("Greenhouse");
  const [blockLabel, setBlockLabel] = useState("Block");
  const [siteMetadata, setSiteMetadata] = useState({});

  // keep active tab in URL ?tab=recommended|requirement
  const initialTab =
    searchParams.get("tab") === TAB_KEYS.REQUIREMENT
      ? TAB_KEYS.REQUIREMENT
      : TAB_KEYS.RECOMMENDED;
  const [activeKey, setActiveKey] = useState(initialTab);

  useEffect(() => {
    // sync on back/forward
    const urlTab = searchParams.get("tab");
    if (urlTab && urlTab !== activeKey) setActiveKey(urlTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, activeKey]);

  const onTabChange = (key) => {
    setActiveKey(key);
    const sp = new URLSearchParams(searchParams);
    sp.set("tab", key);
    setSearchParams(sp);
  };

  const sharedProps = {
    messageApi,
    currentOrg,
    currentSite,
    currentCrop,
    siteMetadata,
    setSiteMetadata,
    greenHouseLabel,
    blockLabel,
  };

  return (
    <>
      <div id="dashboard">
        <Sidebar
          active="spinach_harvest_optimizer"
          sidebarType="SpinachGrower"
          open={sidebarOpen}
          setOpen={setSidebarOpen}
        />

        <div
          className={`flex w-full flex-col pb-14 pt-8 lg:pt-6 sm:pt-4 ${
            sidebarOpen ? "pl-[210px] lg:pl-[50px]" : "pl-[50px]"
          } transition-[padding] duration-200 ease-in-out`}
        >
          <div className="mx-auto w-full max-w-[1900px] px-8 xl:px-6 lg:px-3 md:px-2 sm:px-1.5">
            {contextHolder}

            <Dropdowns
              siteMetadata={siteMetadata}
              setSiteMetadata={setSiteMetadata}
              currentOrg={currentOrg}
              setCurrentOrg={setCurrentOrg}
              orgData={orgData}
              setOrgData={setOrgData}
              orgLoading={orgLoading}
              setOrgLoading={setOrgLoading}
              currentSite={currentSite}
              setCurrentSite={setCurrentSite}
              siteData={siteData}
              setSiteData={setSiteData}
              siteLoading={siteLoading}
              setSiteLoading={setSiteLoading}
              currentCrop={currentCrop}
              setCurrentCrop={setCurrentCrop}
              cropData={cropData}
              setCropData={setCropData}
              cropLoading={cropLoading}
              setCropLoading={setCropLoading}
              searchParams={searchParams}
              setSearchParams={setSearchParams}
              settingDefaults={settingDefaults}
              setSettingDefaults={setSettingDefaults}
              setBlockLabel={setBlockLabel}
              blockLabel={blockLabel}
              greenHouseLabel={greenHouseLabel}
              setGreenHouseLabel={setGreenHouseLabel}
            />

            <Tabs
              activeKey={activeKey}
              onChange={onTabChange}
              type="card"
              items={[
                {
                  key: TAB_KEYS.RECOMMENDED,
                  label: (
                    <span className="flex items-center gap-2">
                      <CalendarOutlined />
                      Recommended Harvest Schedule
                    </span>
                  ),
                  children: <RecommendedHarvestSchedule {...sharedProps} />,
                },
                {
                  key: TAB_KEYS.REQUIREMENT,
                  label: (
                    <span className="flex items-center gap-2">
                      <DeploymentUnitOutlined />
                      Harvest Requirement
                    </span>
                  ),
                  children: <HarvestRequirement {...sharedProps} />,
                },
                {
                  key: TAB_KEYS.CONFIG,
                  label: (
                    <span className="flex items-center gap-2">
                      <SettingOutlined />
                      Configuration
                    </span>
                  ),
                  children: <HarvestConfig {...sharedProps} />,
                },
              ]}
            />
          </div>
        </div>
        <Footer sidebarOpen={sidebarOpen} />
      </div>
    </>
  );
};

export default HarvestOptimizer;
